import { absenceTypeBalance } from './absenceTypeBalance';
import type { AbsenceTimeOffBalance, AbsenceWorker } from './model';

export const absenceBalancePageSize = 20;
export type WorkerBalanceResult =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'ready'; balances: AbsenceTimeOffBalance[] };
export type EmployeeBalanceRow = {
  id: string;
  worker: AbsenceWorker;
  state: 'balance' | 'loading' | 'error' | 'unassigned' | 'type-unassigned';
  balance?: AbsenceTimeOffBalance;
  message?: string;
};

export function filterBalanceWorkers(workers: readonly AbsenceWorker[], search: string, organizationId: string): AbsenceWorker[] {
  const query = search.trim().toLocaleLowerCase('ko');
  return workers.filter((worker) => (!organizationId || (organizationId === '__unassigned__' ? !worker.organization : worker.organization?.id === organizationId))
    && (!query || [worker.name, worker.workerNumber, worker.organization?.descriptor].some((value) => value?.toLocaleLowerCase('ko').includes(query))));
}

export function employeeBalanceRows(workers: readonly AbsenceWorker[], results: Readonly<Record<string, WorkerBalanceResult>>, typeId: string): EmployeeBalanceRow[] {
  return workers.flatMap((worker): EmployeeBalanceRow[] => {
    const result = results[worker.id];
    if (!result || result.state === 'loading') return [{ id: `${worker.id}:loading`, worker, state: 'loading' }];
    if (result.state === 'error') return [{ id: `${worker.id}:error`, worker, state: 'error', message: result.message }];
    const balances = result.balances.filter((balance) => !typeId || balance.timeOffType.id === typeId);
    if (!balances.length) return [{ id: `${worker.id}:empty`, worker, state: typeId ? 'type-unassigned' : 'unassigned' }];
    return balances.map((balance) => ({ id: `${worker.id}:${balance.entitlement.id}`, worker, state: 'balance', balance }));
  });
}

/** 한 정책 안의 발생·이월·지급·조정만 합산하며, 서로 다른 정책이나 단위는 합치지 않는다. */
export function awardedBalanceQuantity(balance: AbsenceTimeOffBalance): string | undefined {
  let scaled = 0n;
  for (const value of [balance.accrued, balance.carryOver, balance.granted, balance.adjusted]) {
    const match = /^(-?)(\d+)(?:\.(\d{1,4}))?$/.exec(value);
    if (!match) return undefined;
    scaled += (match[1] ? -1n : 1n) * (BigInt(match[2]) * 10000n + BigInt((match[3] ?? '').padEnd(4, '0')));
  }
  const absolute = scaled < 0n ? -scaled : scaled;
  const fraction = String(absolute % 10000n).padStart(4, '0').replace(/0+$/, '');
  return `${scaled < 0n ? '-' : ''}${absolute / 10000n}${fraction ? `.${fraction}` : ''}`;
}

/** 페이지가 바뀌어도 하나의 queue를 유지해 진행 중인 요청까지 동시 실행 수에 포함한다. */
export function createBalanceRequestQueue(concurrency = 4) {
  if (!Number.isInteger(concurrency) || concurrency < 1) throw new Error('동시 조회 수는 양의 정수여야 합니다.');
  const waiting: (() => void)[] = [];
  let active = 0;
  const drain = () => {
    while (active < concurrency && waiting.length) waiting.shift()!();
  };
  return function enqueue<T>(load: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      waiting.push(() => {
        active += 1;
        void Promise.resolve().then(load).then(resolve, reject).finally(() => { active -= 1; drain(); });
      });
      drain();
    });
  };
}

export type BalanceAttention = '' | 'low' | 'unassigned' | 'error';
export type BalanceOrder = '' | 'available-asc' | 'available-desc';

/** 전체 직원의 조회 결과에 필터와 정렬을 먼저 적용하고 화면에서 마지막에 페이지를 나눈다. */
export function selectBalanceWorkers(workers: readonly AbsenceWorker[], results: Readonly<Record<string, WorkerBalanceResult>>, typeId: string, attention: BalanceAttention, order: BalanceOrder): AbsenceWorker[] {
  const aggregate = (worker: AbsenceWorker) => {
    const value = results[worker.id];
    return value?.state === 'ready' && typeId ? absenceTypeBalance(value.balances, typeId) : undefined;
  };
  const values = workers.filter((worker) => {
    const result = results[worker.id];
    if (!attention) return true;
    if (attention === 'error') return result?.state === 'error';
    if (result?.state !== 'ready') return false;
    const balances = result.balances.filter((balance) => !typeId || balance.timeOffType.id === typeId);
    if (attention === 'unassigned') return !balances.length;
    const types = [...new Set(balances.map((balance) => balance.timeOffType.id))];
    return types.some((id) => { const balance = absenceTypeBalance(balances, id); return balance?.unitOfTime === 'DAYS' && Number(balance.available) <= 2; });
  });
  if (!typeId || !order) return values;
  return values.sort((left, right) => {
    const a = aggregate(left); const b = aggregate(right);
    if (!a || !b) return a ? -1 : b ? 1 : left.name.localeCompare(right.name, 'ko');
    // 과거 정책의 단위가 다르면 단위별로 묶고 수량을 비교한다.
    return a.unitOfTime.localeCompare(b.unitOfTime) || (Number(a.available) - Number(b.available)) * (order === 'available-desc' ? -1 : 1) || left.name.localeCompare(right.name, 'ko');
  });
}
